import { useEffect, useMemo, useState } from "react";
import { addYears, format, isValid, isBefore } from "date-fns";
import { EVENT_DATE_FORMAT, parseCalendarValueAsString } from "shared/utils";
import i18n, { i18nLanguage } from "i18n";
import { es, fr } from "date-fns/locale";
import { Box, Flex, FlexProps, BoxProps } from "styled-system/jsx";
import {
  Button,
  ButtonProps,
  CalendarPopover,
  DateValue,
  Icon,
  parseDate,
  Text,
  TextProps,
} from "@ttc3k/trekker";
import { Calendar } from "iconoir-react";
import { dateInputRecipe } from "./dateInput.recipe";
import { toZonedTime } from "date-fns-tz";
import { cx } from "styled-system/css";

type DateInputProps = {
  value: string | undefined;
  title?: string;
  onValueChange: (value: string) => void;
  min?: string;
  max?: string;
  onBlur?: () => void;
  error?: string;
  displayProps?: TextProps;
  triggerProps?: ButtonProps;
  onClearDatesClick?: () => void;
  clearDatesLabel?: string;
  closeOnSameDayClick?: boolean;
  canSelectPastDates?: boolean;
  allowYearView?: boolean;
  hideIcon?: boolean;
  containerProps?: FlexProps;
  /** Render the calendar in a portal so it is not clipped inside overflow-hidden modals. */
  usePortal?: boolean;
  /** z-index for the portaled calendar popover (use `toast` inside modals). */
  popoverZIndex?: string;
  /**
   * Force the trigger to stay full width even when no value is selected yet.
   * (The `empty` recipe variant normally shrinks the trigger to a small
   * icon-only square, which is undesirable when rendering fields side by
   * side, e.g. in a date-range grid.)
   */
  fullWidth?: boolean;
  /** Open the calendar immediately when the input mounts (e.g. right after
   * the user chooses a "custom date" option that reveals this input). */
  autoOpen?: boolean;
  /** Render only the trigger (no label column layout) for inline filter bars. */
  compact?: boolean;
  /** Muted text shown in the trigger while no date is selected. */
  placeholder?: string;
};

export const DateInput = ({
  value,
  title,
  onValueChange,
  min,
  max,
  onBlur,
  error,
  displayProps,
  triggerProps,
  onClearDatesClick,
  clearDatesLabel,
  closeOnSameDayClick,
  canSelectPastDates,
  allowYearView,
  hideIcon,
  containerProps,
  usePortal = false,
  popoverZIndex,
  fullWidth = false,
  autoOpen = false,
  compact = false,
  placeholder,
}: DateInputProps) => {
  const [isOpen, setIsOpen] = useState(autoOpen);

  // Helper function to safely format dates
  const formatSafeDate = (dateString: string) => {
    try {
      const date = toZonedTime(dateString, "UTC");
      return format(date, EVENT_DATE_FORMAT);
    } catch {
      return dateString;
    }
  };

  const formattedValue = useMemo(() => {
    const locale =
      i18n.language === i18nLanguage.fr
        ? fr
        : i18n.language === i18nLanguage.es
          ? es
          : undefined;

    if (!value) return "";

    try {
      const date = toZonedTime(value, "UTC");
      return isValid(date) ? format(date, "MMM d, yyyy", { locale }) : "";
    } catch {
      return "";
    }
  }, [value]);

  const today = useMemo(() => format(new Date(), EVENT_DATE_FORMAT), []);

  const minDate = useMemo(
    () =>
      min ? formatSafeDate(min) : !canSelectPastDates ? today : "2025-01-01", // Leave this min date as a default to avoid bug within Ark calendar component
    [min, today, canSelectPastDates],
  );

  // Max date is 5 years from today
  const maxDate = useMemo(
    () =>
      max
        ? formatSafeDate(max)
        : format(addYears(new Date(), 5), EVENT_DATE_FORMAT),
    [max],
  );

  // Start the calendar in the min month
  const [focusedValue, setFocusedValue] = useState<string | undefined>(
    undefined,
  );
  useEffect(() => {
    // If there is a value, we'll focus on that date
    // Otherwise, we'll focus on the greater of the min date or today
    setFocusedValue(value ? value : isBefore(today, minDate) ? minDate : today);
    // The focused value needs to be cleared after a short delay because
    // the calendar will not let you change the month if the focused value is set.
    setTimeout(() => setFocusedValue(undefined), 100);
  }, [minDate, value, today]);

  const handleBlur = () => {
    if (!isOpen) onBlur?.();
  };

  const isEmpty = !formattedValue;
  const buttonClasses = dateInputRecipe({
    error: !!error,
    empty: isEmpty && !fullWidth && !placeholder,
  });

  const calendar = (
    <>
      {isOpen && !usePortal && (
        <Box
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          top="0"
          zIndex="10"
          onClick={() => setIsOpen(false)}
        />
      )}
      <CalendarPopover
        // When the open state changes, we can call the onBlur prop
        onOpenChange={({ open }: { open: boolean }) => !open && onBlur?.()}
        selectionMode={"single"}
        tabIndex={0}
        value={
          value ? [formatSafeDate(value)].map(date => parseDate(date)) : []
        }
        closeOnSelect={true}
        // closeOnSelect doesn't seem to work when selecting the same day
        // This new prop in the Trekker calendar will cause a click event to be triggered
        // on the date trigger. If the date is the same AND we want to close when clicking the same day,
        // we can use this prop to close the calendar.
        {...(closeOnSameDayClick && {
          onDayClick: (date: DateValue) => {
            if (
              value ===
              date.year +
                "-" +
                date.month.toString().padStart(2, "0") +
                "-" +
                date.day.toString().padStart(2, "0")
            ) {
              setIsOpen(false);
            }
          },
        })}
        onValueChange={({ valueAsString }: { valueAsString: string[] }) => {
          onValueChange(parseCalendarValueAsString(valueAsString)[0]);
          setIsOpen(false);
        }}
        // Conditionally add the clear date props so that the clear date button is only shown when these exist
        {...(onClearDatesClick && {
          onClearDatesClick: () => {
            onClearDatesClick?.();
            setIsOpen(false);
          },
          ctaClearDatesProps: { type: "button" },
        })}
        {...(clearDatesLabel && { clearDatesLabel })}
        focusedValue={focusedValue ? parseDate(focusedValue) : undefined}
        min={minDate ? parseDate(minDate) : undefined}
        max={maxDate ? parseDate(maxDate) : undefined}
        Trigger={
          <>
            <Button
              visual="primary"
              mode="light"
              type="button"
              onClick={() => setIsOpen(prev => !prev)}
              onBlurCapture={handleBlur}
              className={cx(buttonClasses, triggerProps?.className)}
              textAlign={"left"}
              px={hideIcon ? "150" : undefined}
              h={compact ? "38px" : undefined}
              minH={compact ? "38px" : undefined}
              maxH={compact ? "38px" : undefined}
              w={compact ? "full" : undefined}
              {...triggerProps}
            >
              {!isEmpty && (
                <Text
                  visual="smallMedium"
                  flexGrow={1}
                  minH="22px"
                  {...displayProps}
                >
                  {formattedValue}
                </Text>
              )}
              {isEmpty && placeholder && (
                <Text
                  visual="smallRegular"
                  color="text.light"
                  flexGrow={1}
                  minH="22px"
                >
                  {placeholder}
                </Text>
              )}
              {!hideIcon && (
                <Icon
                  Element={Calendar}
                  color="icon.light"
                  width="16px"
                  height="16px"
                  flexShrink={0}
                />
              )}
            </Button>
            {error && (
              <Text visual="smallMedium" color="text.error.dark" mt="3px">
                {error}
              </Text>
            )}
          </>
        }
        disablePastDates={!canSelectPastDates}
        allowYearView={allowYearView}
        open={isOpen}
        outline="none"
        {...(usePortal ? {} : { noPortal: true })}
        width="full"
        zIndex={isOpen ? (popoverZIndex ?? (usePortal ? "toast" : "11")) : 0}
      />
    </>
  );

  if (compact) {
    return (
      <Box position="relative" w="full" h="full" {...(containerProps as BoxProps)}>
        {calendar}
      </Box>
    );
  }

  return (
    <Flex
      flexDir="column"
      gap="50"
      width="full"
      position="relative"
      {...containerProps}
    >
      {title && (
        <Text visual="smallMedium" color="text.mid">
          {title}
        </Text>
      )}

      {calendar}
    </Flex>
  );
};
